import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from '@/components/forge/Chip'
import { Field, TextArea } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { LinkField } from '@/components/forge/LinkField'
import { PlaceField } from '@/components/forge/PlaceField'
import { Eyebrow } from '@/components/forge/primitives'
import {
  DEFAULT_CATEGORY,
  DEFAULT_SEVERITY,
  LOGBOOK_CATEGORIES,
  LOGBOOK_SEVERITIES,
  OBSERVATION_MAX_LENGTH,
  TITLE_MAX_LENGTH,
  fromNaiveUtc,
  todayISO,
} from '@/features/logbook/api'
import { placeOf, trioOf } from '@/features/hierarchy/place'
import { LogbookPhotoField, photosSettled } from './LogbookPhotoField'
import { LogbookSensorTagField, sensorTagsValid } from './LogbookSensorTagField'

/**
 * Write a log book entry, or change one.
 *
 * LIVE — every field `LogBookEntryCreate` takes, and nothing it does not. Given
 * an `entry`, it is the edit: seeded from the detail, with the place, the shift
 * and the shift date shown and locked — `LogBookEntryUpdate` does not take
 * them. The photos and signal windows are seeded too, because the `PUT` is a
 * full replace and an empty list sent back would detach them all.
 *
 * ── A modal, where the demo had an inline composer ───────────────────────
 * The prototype writes into a card above the feed, and for the note it was
 * collecting — a line of prose and a machine — that was the better call: the
 * cheapest thing on the screen should not interrupt reading it.
 *
 * The real create is nine fields, two of them lists, one of them a file upload.
 * An inline card that tall pushes the feed off the screen and leaves a
 * half-written entry one stray click from being lost. So it is a dialog, the
 * same one `WorkOrderFormDialog` is, and the two writes in this product that
 * carry this much now look and behave alike. `LogbookComposer` is kept beside
 * this file rather than deleted — see its header.
 *
 * ── The author is not a field, and now genuinely cannot be ───────────────
 * `LogBookEntryCreate` has no author on it at all: `created_by` is stamped from
 * the token. The demo composer showed a byline because the demo list sent one
 * down; `LogBookEntryOut` does not, so there is nothing truthful to print here
 * and nothing is printed. Recorded in BACKEND-GAPS.md.
 *
 * ── The parent may be a site, a line OR an asset ─────────────────────────
 * `ExactlyOneParentMixin` takes any one of the three, and a logbook genuinely
 * uses all three: a safety note about a site is not about a machine on it. So
 * the picker offers every tier the API accepts and encodes which one it
 * returned, rather than quietly narrowing to assets the way the work-order form
 * does — there, an order is nearly always raised against a machine.
 *
 * ── The anomaly and the work order sit at exactly this place ────────────
 * `create_logbook_entry_action` runs `verify_linked_rows` on both: a link must
 * be recorded at EXACTLY the entry's place, and the reader needs the other
 * module's grant there. So both are chosen in `LinkPickerDialog`, standing at
 * the entry's place, and changing the place clears them. The selects this
 * replaced offered the estate's first 200 open anomalies and work orders, most
 * of which that save refused.
 */
export function LogbookFormDialog({
  open,
  entry,
  shifts,
  shiftsFailed,
  canPickAnomaly,
  canPickWorkOrder,
  canReadSensors,
  pending,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation(['app', 'common'])

  /* Initialised once per opening. The PARENT gives this component a `key` that
     changes when it opens, so React remounts it and this runs again — rather
     than an effect setting state during render, which is the cascading render
     the lint rule is about and the arrangement `WorkOrderFormDialog` arrived at. */
  const editing = Boolean(entry?.id)
  const [form, setForm] = useState(() => seed(entry, t))
  const [photos, setPhotos] = useState(() => seedPhotos(entry, t))
  /* How many photos are still going up. Held HERE and not in the field,
     because Save depends on it: a photo mid-upload is not yet a row in
     `photos`, so a gate that only inspected the list would let the create go
     out without it — and an entry with no photos is a valid entry, so nothing
     downstream would report the loss. */
  const [uploading, setUploading] = useState(0)
  const [sensorTags, setSensorTags] = useState(() => seedSensorTags(entry))

  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }))

  const parent = trioOf(form.place)

  /**
   * Everything the server would refuse, refused here first.
   *
   * `title` and `observation` are `min_length=1` after strip; the parent is
   * `ExactlyOneParentMixin`, which answers FastAPI's bare 422 rather than the
   * field-keyed envelope, so a refusal from it arrives with nothing to attach
   * to a control; `shift_id` and `shift_date` are non-null columns.
   *
   * The two list fields gate as well: a photo whose upload failed carries no
   * id, a photo still in flight is not in the list yet, and a tag row without a
   * signal, or with a window that runs backwards, would be stored as written —
   * the server has no validator on the pair.
   */
  const valid =
    form.title.trim().length > 0 &&
    form.observation.trim().length > 0 &&
    Boolean(parent) &&
    Boolean(form.shiftId) &&
    Boolean(form.shiftDate) &&
    photosSettled(photos, uploading) &&
    sensorTagsValid(sensorTags)

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      size="md"
      title={editing ? t('logbook.form.editTitle', { id: entry.serialNumber }) : t('logbook.form.title')}
      description={editing ? t('logbook.form.editDescription') : t('logbook.form.description')}
      footer={
        <div className="flex flex-1 flex-wrap justify-end gap-2">
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
          <ForgeButton
            variant="primary"
            disabled={!valid || pending}
            onClick={() =>
              onSubmit({
                title: form.title,
                observation: form.observation,
                category: form.category,
                severity: form.severity,
                /* The edit sends the id and none of the Create-only keys —
                   each is a 400 on the `PUT`. The create sends exactly one of
                   the three parents with all three keys present; see
                   `createLogbookEntry` for why the nulls are not omitted. */
                ...(editing
                  ? { id: entry.id }
                  : { ...parent, shiftId: form.shiftId, shiftDate: form.shiftDate }),
                anomalyId: form.anomaly[0]?.id ?? null,
                workOrderId: form.workOrder[0]?.id ?? null,
                photoFileIds: photos.map((photo) => photo.fileId),
                sensorTags,
              })
            }
          >
            {editing ? t('logbook.form.saveChanges') : t('logbook.form.submit')}
          </ForgeButton>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label={t('logbook.form.entryTitle')}
          required
          maxLength={TITLE_MAX_LENGTH}
          value={form.title}
          onChange={(event) => set('title', event.target.value)}
          placeholder={t('logbook.form.entryTitlePlaceholder')}
          hint={t('logbook.form.entryTitleHint')}
        />

        <TextArea
          rows={5}
          label={t('logbook.form.observation')}
          required
          maxLength={OBSERVATION_MAX_LENGTH}
          value={form.observation}
          onChange={(event) => set('observation', event.target.value)}
          placeholder={t('logbook.form.observationPlaceholder')}
          hint={t('logbook.form.observationHint')}
        />

        {/* Chips rather than two selects, as the work-order form does for
            priority: eight and four are both small enough to read at once, and
            a category chosen from a closed dropdown is one nobody compares. */}
        <div>
          <Eyebrow className="mb-2">{t('logbook.form.category')}</Eyebrow>
          <div role="group" aria-label={t('logbook.form.category')} className="flex flex-wrap gap-1.5">
            {LOGBOOK_CATEGORIES.map((category) => (
              <Chip
                key={category}
                selected={form.category === category}
                onClick={() => set('category', category)}
              >
                {t(`logbook.category.${category}`)}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <Eyebrow className="mb-2">{t('logbook.form.severity')}</Eyebrow>
          <div role="group" aria-label={t('logbook.form.severity')} className="flex flex-wrap gap-1.5">
            {LOGBOOK_SEVERITIES.map((severity) => (
              <Chip
                key={severity}
                selected={form.severity === severity}
                onClick={() => set('severity', severity)}
              >
                {t(`common:severity.${severity}`)}
              </Chip>
            ))}
          </div>
        </div>

        {/* Every tier the API will attach to, through the same drill-down
            dialog every place picker in the app opens — see `PlaceField`. The
            trigger names the tier and the parent, so "Line 3" and a machine
            called "Line 3 Infeed" cannot be confused. */}
        <PlaceField
          label={t('logbook.form.asset')}
          required
          disabled={editing}
          value={form.place}
          onChange={(value) => {
            /* Links must sit at exactly the place — those chosen for the one
               just replaced would be refused. */
            setForm((previous) => ({ ...previous, place: value, anomaly: [], workOrder: [] }))
            /* Tags hang off the machine that was just replaced. Clearing them
               is the honest move: keeping them would attach one asset's signals
               to a note about another, which is the kind of wrong nothing
               downstream can detect. */
            setSensorTags([])
          }}
          placeholder={t('logbook.form.assetPlaceholder')}
          hint={editing ? t('logbook.form.fixedHint') : t('logbook.form.assetHint')}
        />

        {/* ── Which shift, and on which day ────────────────────────────────
            Two fields because the API has two, and they answer different
            questions: `shift_id` is the PATTERN from Shift Master — Nights,
            06:00–14:00 — and `shift_date` is the calendar day it ran on. A
            night shift crossing midnight is one shift on one date, which is
            precisely why the date is not derived from the clock. */}
        <div className="grid gap-4 sm:grid-cols-2">
          {shiftsFailed && !editing ? (
            <p className="m-0 text-body-sm text-on-amber">{t('logbook.form.shiftsFailed')}</p>
          ) : (
            <ForgeSelect
              label={t('logbook.form.shift')}
              required
              disabled={editing}
              value={form.shiftId}
              onValueChange={(value) => set('shiftId', value)}
              placeholder={t('logbook.form.shiftPlaceholder')}
              options={shiftOptions(shifts, entry)}
            />
          )}
          <Field
            label={t('logbook.form.shiftDate')}
            type="date"
            required
            disabled={editing}
            value={form.shiftDate}
            onChange={(event) => set('shiftDate', event.target.value)}
            hint={editing ? undefined : t('logbook.form.shiftDateHint')}
          />
        </div>

        {/* ── The two records a note may point at ──────────────────────────
            Both optional columns, both behind a permission this one does not
            imply — `anomalies.manage`, and `work_orders.manage`. Stated when
            not held, rather than drawn as an empty picker, which reads as
            "there are none". */}
        {LINKS.map(({ key, kind }) => (
          <LinkField
            key={key}
            kind={kind}
            single
            label={t(`logbook.form.${key}`)}
            place={form.place}
            trio={parent}
            value={form[key]}
            onChange={(items) => set(key, items)}
            hint={t(`logbook.form.${key}Hint`)}
            blocked={
              !parent
                ? t('logbook.form.pickPlaceFirst')
                : !(key === 'anomaly' ? canPickAnomaly : canPickWorkOrder)
                  ? t(`logbook.form.${key}NoGrant`)
                  : undefined
            }
          />
        ))}

        <LogbookPhotoField
          photos={photos}
          onChange={setPhotos}
          uploading={uploading}
          onUploadingChange={setUploading}
          disabled={pending}
        />

        <LogbookSensorTagField
          /* Tags hang off a machine. A site- or line-level entry has none to
             offer, and the field says so rather than showing an empty picker. */
          machineId={parent?.machineId ?? null}
          rows={sensorTags}
          onChange={setSensorTags}
          canRead={canReadSensors}
          disabled={pending}
        />
      </div>
    </ForgeDialog>
  )
}

const LINKS = [
  { key: 'anomaly', kind: 'anomalies' },
  { key: 'workOrder', kind: 'workOrders' },
]

function seed(entry, t) {
  if (entry?.id) {
    return {
      title: entry.title ?? '',
      observation: entry.observation ?? '',
      category: entry.category ?? DEFAULT_CATEGORY,
      severity: entry.severity ?? DEFAULT_SEVERITY,
      place: placeOf(entry),
      shiftId: entry.shiftId ?? '',
      shiftDate: entry.shiftDate ?? '',
      /* A link whose target was deleted serves its id with no brief. It is kept
         and sent back — the server re-checks only links that are NEW on the
         entry — and shown as gone rather than dropped behind the writer's back. */
      anomaly: entry.anomalyId
        ? [{
            id: entry.anomalyId,
            serialNumber: entry.anomaly?.serialNumber ?? '',
            title: entry.anomaly?.title ?? t('logbook.detail.linkGone'),
          }]
        : [],
      workOrder: entry.workOrderId
        ? [{
            id: entry.workOrderId,
            serialNumber: entry.workOrder?.serialNumber ?? '',
            title: entry.workOrder?.title ?? t('logbook.detail.linkGone'),
          }]
        : [],
    }
  }
  return {
    title: '',
    observation: '',
    category: DEFAULT_CATEGORY,
    severity: DEFAULT_SEVERITY,
    place: null,
    shiftId: '',
    /* Today, because a note is nearly always written on the day it happened.
       A night shift written up after midnight is the case this gets wrong, and
       it is a date field the writer can correct rather than a derivation they
       cannot see. */
    shiftDate: todayISO(),
    anomaly: [],
    workOrder: [],
  }
}

/**
 * The photos already on an entry, as the field holds an uploaded one. The
 * presigned `viewUrl` stands in for the local preview; `URL.revokeObjectURL` on
 * an http address is a no-op, so removing one is safe. The API serves no file
 * name, so each is numbered as the detail page numbers it.
 */
function seedPhotos(entry, t) {
  return (entry?.photos ?? []).map((photo, index) => ({
    fileId: photo.fileId,
    name: t('logbook.detail.photoAlt', { n: index + 1 }),
    previewUrl: photo.viewUrl,
    error: null,
  }))
}

/**
 * The windows already pinned, in the control's local wall-clock form, each
 * remembering the server's own value — see `windowOut` in the feature module
 * for why an untouched window must go back byte-for-byte.
 */
function seedSensorTags(entry) {
  return (entry?.sensorTags ?? []).map((tag) => {
    const start = fromNaiveUtc(tag.windowStart)
    const end = fromNaiveUtc(tag.windowEnd)
    return {
      sensorTagId: tag.sensorTagId,
      windowStart: start,
      windowEnd: end,
      seed: {
        start: { local: start, wire: tag.windowStart },
        end: { local: end, wire: tag.windowEnd },
      },
    }
  })
}

/** Shift Master's patterns, plus the entry's own if the list does not have it. */
function shiftOptions(shifts, entry) {
  const options = shifts.map((shift) => ({
    value: shift.id,
    label: shift.start && shift.end ? `${shift.name} · ${shift.start}–${shift.end}` : shift.name,
  }))
  if (entry?.shiftId && !options.some((option) => option.value === entry.shiftId)) {
    options.unshift({ value: entry.shiftId, label: entry.shift?.name ?? entry.shiftId })
  }
  return options
}
